import React, { useEffect, useRef, useState } from 'react';
import Modal from './Modal';
import Button from './Button';

interface CameraCaptureModalProps {
  onCapture: (file: File) => void;
  onClose: () => void;
}

// Real webcam capture via getUserMedia — works on both desktop and mobile
// browsers with camera access (unlike a plain <input capture>, which only
// launches a camera app on mobile OSes and is a no-op file-picker on desktop).
// Requires a secure context (HTTPS or localhost).
export default function CameraCaptureModal({ onCapture, onClose }: CameraCaptureModalProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [photoDataUrl, setPhotoDataUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('This browser does not support camera access.');
      return;
    }
    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          void videoRef.current.play().catch(() => {});
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.name === 'NotAllowedError' ? 'Camera access was denied.' : 'Could not access the camera.');
      });
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    setPhotoDataUrl(canvas.toDataURL('image/jpeg', 0.92));
  };

  const handleUse = () => {
    if (!photoDataUrl) return;
    fetch(photoDataUrl)
      .then((res) => res.blob())
      .then((blob) => onCapture(new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' })));
  };

  return (
    <Modal
      title="Take a photo"
      onClose={onClose}
      footer={
        photoDataUrl ? (
          <>
            <Button variant="ghost" onClick={() => setPhotoDataUrl(null)}>Retake</Button>
            <Button variant="accent" onClick={handleUse}>Use this photo</Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button variant="accent" onClick={handleCapture} disabled={!!error}>Capture</Button>
          </>
        )
      }
    >
      {error ? (
        <p style={{ color: 'var(--danger, #dc2626)', fontSize: 'var(--fs-sm)' }}>{error}</p>
      ) : photoDataUrl ? (
        <img src={photoDataUrl} alt="Captured" style={{ width: '100%', borderRadius: 12, display: 'block' }} />
      ) : (
        <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', borderRadius: 12, display: 'block', background: '#000' }} />
      )}
    </Modal>
  );
}
